import { LocalAeqdProjection } from "@map2threejs/core/projection";
import {
  chooseImageryZoom,
  latLngToTileFraction,
  normalizeTileX,
  tileRangesForRegion,
} from "@map2threejs/core/geo";
import type {
  BuildingFeature,
  HeightTile,
  MapDataProvider,
  ProviderCapabilities,
  RasterTile,
  RegionRequest,
} from "@map2threejs/core/types";

const DEMO_ATTRIBUTION = "Demo fixture · © MapTiler © OpenStreetMap contributors";

function terrainValue(globalX: number, globalY: number): number {
  return 78 + Math.sin(globalX * 0.018) * 4.2 + Math.cos(globalY * 0.015) * 3.4 + Math.sin((globalX + globalY) * 0.009) * 1.8;
}

function makeTerrainTile(z: number, x: number, y: number, tileSize: number): HeightTile {
  const elevations = new Float32Array(tileSize * tileSize);
  for (let py = 0; py < tileSize; py += 1) {
    for (let px = 0; px < tileSize; px += 1) {
      elevations[py * tileSize + px] = terrainValue(x * tileSize + px, y * tileSize + py);
    }
  }
  return { z, x, y, width: tileSize, height: tileSize, elevations };
}

async function makeImageryTile(tileSize: number, x: number, y: number): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = tileSize;
  canvas.height = tileSize;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Demo imagery canvas unavailable.");
  context.fillStyle = "#556258";
  context.fillRect(0, 0, tileSize, tileSize);
  context.fillStyle = "#465b49";
  for (let row = 0; row < 6; row += 1) {
    for (let column = 0; column < 6; column += 1) {
      if ((row + column + x + y) % 4 === 0) continue;
      const left = column * 46 - 10;
      const top = row * 44 - 8;
      context.fillRect(left + 7, top + 7, 30 + ((row + x) % 3) * 3, 25 + ((column + y) % 2) * 5);
      context.strokeStyle = "#70746d";
      context.lineWidth = 2;
      context.strokeRect(left + 7, top + 7, 30 + ((row + x) % 3) * 3, 25 + ((column + y) % 2) * 5);
    }
  }
  context.strokeStyle = "#87877e";
  context.lineWidth = 12;
  context.beginPath();
  context.moveTo(-20, tileSize * 0.72);
  context.lineTo(tileSize + 20, tileSize * 0.24);
  context.stroke();
  context.strokeStyle = "#a5a394";
  context.lineWidth = 2;
  context.stroke();
  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Demo imagery encoding failed."))), "image/webp"),
  );
}

function makeBuildings(request: RegionRequest): BuildingFeature[] {
  const projection = new LocalAeqdProjection(request.center);
  const features: BuildingFeature[] = [];
  const colors = ["#c8c2b5", "#a8aaa5", "#d5c9b8", "#9c9388", "#d6d4ce"];
  const roofs = [
    "flat",
    "gabled",
    "gambrel",
    "half_hipped",
    "hipped",
    "mansard",
    "pyramidal",
    "dome",
    "onion",
    "round",
    "saltbox",
    "sawtooth",
    "skillion",
    "spherical",
  ];
  let id = 0;
  for (let north = -request.radiusMeters; north <= request.radiusMeters; north += 42) {
    for (let east = -request.radiusMeters; east <= request.radiusMeters; east += 38) {
      const jitterX = Math.sin((east + north) * 0.043) * 7;
      const jitterN = Math.cos((east - north) * 0.037) * 6;
      const cx = east + jitterX;
      const cn = north + jitterN;
      if (Math.hypot(cx, cn) > request.radiusMeters - 12) continue;
      if (Math.abs(cx + cn * 0.58) < 12) continue;
      if (Math.abs(cn - request.radiusMeters * 0.18) < 8) continue;
      const waterCenterX = request.radiusMeters * 0.39 - cn * 0.31;
      if (Math.abs(cx - waterCenterX) < request.radiusMeters * 0.17) continue;
      const width = 17 + (id % 4) * 3;
      const depth = 15 + (id % 3) * 4;
      const local = [
        [cx - width / 2, cn - depth / 2],
        [cx + width / 2, cn - depth / 2],
        [cx + width / 2, cn + depth / 2],
        [cx - width / 2, cn + depth / 2],
        [cx - width / 2, cn - depth / 2],
      ] as const;
      const feature: BuildingFeature = {
        id: `demo-${id}`,
        polygons: [{ outer: local.map(([x, n]) => projection.inverse(x, n)), holes: [] }],
        facadeColor: colors[id % colors.length] ?? "#c8c2b5",
        roofColor: colors[(id + 2) % colors.length] ?? "#d5c9b8",
        roofShape: roofs[id % roofs.length] ?? "flat",
      };
      if (id % 13 !== 0) feature.heightMeters = 7 + (id % 8) * 3.2;
      features.push(feature);
      id += 1;
    }
  }
  const toGeoRing = (points: ReadonlyArray<readonly [number, number]>) => (
    points.map(([east, north]) => projection.inverse(east, north))
  );
  features.push({
    id: "demo-complex-l-roof",
    polygons: [{
      outer: toGeoRing([
        [-30, -24],
        [30, -24],
        [30, -6],
        [8, -6],
        [8, 24],
        [-30, 24],
        [-30, -24],
      ]),
      holes: [],
    }],
    heightMeters: 24,
    facadeColor: "#a99c8a",
    roofColor: "#a96f59",
    roofShape: "gabled",
  });
  features.push({
    id: "demo-courtyard-dome",
    polygons: [{
      outer: toGeoRing([
        [-62, 34],
        [-8, 34],
        [-8, 86],
        [-62, 86],
        [-62, 34],
      ]),
      holes: [toGeoRing([
        [-49, 47],
        [-21, 47],
        [-21, 73],
        [-49, 73],
        [-49, 47],
      ])],
    }],
    heightMeters: 20,
    facadeColor: "#a7a7a0",
    roofColor: "#88715f",
    roofShape: "dome",
  });
  return features;
}

export class DemoMapProvider implements MapDataProvider {
  async validate(signal: AbortSignal): Promise<ProviderCapabilities> {
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    return {
      imagery: { minZoom: 0, maxZoom: 19, tileSize: 256, format: "webp" },
      terrain: {
        minZoom: 0,
        maxZoom: 14,
        tileSize: 256,
        format: "fixture",
        horizontalResolutionMeters: 30,
        verticalResolutionMeters: 6,
      },
      buildings: { minZoom: 12, maxZoom: 15, tileSize: 4096, format: "mvt" },
      vector: { minZoom: 0, maxZoom: 15, tileSize: 4096, format: "mvt" },
      attribution: DEMO_ATTRIBUTION,
    };
  }

  async loadImagery(request: RegionRequest) {
    const choice = chooseImageryZoom({
      center: request.center,
      radiusMeters: request.radiusMeters,
      minZoom: 0,
      maxZoom: 18,
      tileSize: 256,
      targetMetersPerPixel: 0.5,
      maxTextureSize: 4096,
      maxTiles: 64,
    });
    const range = tileRangesForRegion(request.center, request.radiusMeters, choice.zoom)[0];
    if (!range) throw new Error("Demo range unavailable.");
    const tasks: Promise<RasterTile>[] = [];
    for (let y = range.minY; y <= range.maxY; y += 1) {
      for (let unwrappedX = range.minX; unwrappedX <= range.maxX; unwrappedX += 1) {
        const x = normalizeTileX(unwrappedX, choice.zoom);
        tasks.push(
          makeImageryTile(256, x, y).then((image) => ({
            z: choice.zoom,
            x,
            y,
            column: unwrappedX - range.minX,
            row: y - range.minY,
            image,
          })),
        );
      }
    }
    return {
      zoom: choice.zoom,
      tileSize: 256,
      columns: choice.columns,
      rows: choice.rows,
      originTileX: range.minX,
      originTileY: range.minY,
      metersPerPixel: choice.metersPerPixel,
      tiles: await Promise.all(tasks),
      attribution: DEMO_ATTRIBUTION,
    };
  }

  async loadTerrain(request: RegionRequest) {
    const zoom = 14;
    const tileSize = 256;
    const range = tileRangesForRegion(request.center, request.radiusMeters, zoom)[0];
    if (!range) throw new Error("Demo terrain range unavailable.");
    const tiles: HeightTile[] = [];
    for (let y = range.minY; y <= range.maxY; y += 1) {
      for (let unwrappedX = range.minX; unwrappedX <= range.maxX; unwrappedX += 1) {
        tiles.push(makeTerrainTile(zoom, normalizeTileX(unwrappedX, zoom), y, tileSize));
      }
    }
    return {
      zoom,
      tileSize,
      tiles,
      horizontalResolutionMeters: 30,
      verticalResolutionMeters: 6,
      attribution: DEMO_ATTRIBUTION,
    };
  }

  async loadBuildings(request: RegionRequest) {
    return makeBuildings(request);
  }

  async loadMapFeatures(request: RegionRequest) {
    const projection = new LocalAeqdProjection(request.center);
    const point = (east: number, north: number) => projection.inverse(east, north);
    const radius = request.radiusMeters;
    return {
      zoom: 15,
      roads: [
        {
          id: "demo-primary-road",
          lines: [[point(-radius, -radius * 0.48), point(radius, radius * 0.32)]],
          polygons: [],
          sourceLayer: "road",
          className: "primary",
          paved: true,
        },
        {
          id: "demo-secondary-road",
          lines: [[point(-radius, radius * 0.18), point(radius, radius * 0.18)]],
          polygons: [],
          sourceLayer: "road",
          className: "secondary",
          paved: true,
        },
        {
          id: "demo-railway",
          lines: [[point(-radius, radius * 0.66), point(radius, radius * 0.46)]],
          polygons: [],
          sourceLayer: "railway",
          className: "rail",
        },
      ],
      waterways: [
        {
          id: "demo-stream",
          lines: [[point(-radius * 0.7, -radius * 0.62), point(radius * 0.58, radius * 0.75)]],
          className: "stream",
        },
      ],
      barriers: [
        {
          id: "demo-wall",
          lines: [[point(-radius * 0.62, radius * 0.08), point(-radius * 0.18, radius * 0.08)]],
          className: "barrier",
          subclass: "wall",
        },
      ],
      surfaces: [
        {
          id: "demo-water",
          sourceLayer: "water",
          category: "water" as const,
          className: "river",
          polygons: [{
            outer: [
              point(radius * 0.38, -radius),
              point(radius * 0.7, -radius),
              point(radius * 0.36, radius),
              point(radius * 0.08, radius),
              point(radius * 0.38, -radius),
            ],
            holes: [],
          }],
        },
        {
          id: "demo-park",
          sourceLayer: "park",
          category: "landcover" as const,
          className: "park",
          polygons: [{
            outer: [
              point(-radius * 0.82, radius * 0.18),
              point(-radius * 0.12, radius * 0.18),
              point(-radius * 0.12, radius * 0.82),
              point(-radius * 0.82, radius * 0.82),
              point(-radius * 0.82, radius * 0.18),
            ],
            holes: [],
          }],
        },
      ],
      streetObjects: [
        { id: "demo-lamp-1", position: point(-42, 26), className: "street", subclass: "street_lamp" },
        { id: "demo-lamp-2", position: point(18, -34), className: "street", subclass: "street_lamp" },
        { id: "demo-bench", position: point(-15, 58), className: "street", subclass: "bench", directionDegrees: 75 },
        { id: "demo-tree", position: point(-72, 64), className: "barrier", subclass: "tree" },
        { id: "demo-signal", position: point(54, 42), className: "street", subclass: "traffic_signals" },
      ],
      attribution: DEMO_ATTRIBUTION,
    };
  }
}

export function demoMapPosition(center: { lat: number; lng: number }, zoom: number): { x: number; y: number } {
  return latLngToTileFraction(center, zoom);
}
